import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import { usePeersStore } from '@src/stores';

import { EAnswerStatus, EAnswerType, EQuestionStatus, EUserType, IStatusLabelProps } from '@src/types';

interface UseQuestionStatusLabelResult {
  statusLabel: string;
  statusType: EQuestionStatus | EAnswerStatus;
}

const useQuestionStatusLabel = ({ question }: IStatusLabelProps): UseQuestionStatusLabelResult => {
  const { currentPeer } = useRoomContext();
  const peers = usePeersStore(useShallow((state) => Array.from(state.peers.values())));

  const { status, answer, assignTo, dismissedBy, assignedBy } = question;

  const result = useMemo(() => {
    const type = status === EQuestionStatus.DISMISSED ? EQuestionStatus.DISMISSED : (answer?.status ?? status);

    const answerType = answer?.answerType;
    const assignedToName = assignTo?.name;
    const isViewer = currentPeer?.peer_info?.user_type === EUserType.ATTENDEE;
    const isCurrentUserSpeaker = currentPeer?.peer_info?.user_type === EUserType.SPEAKER;
    const isCurrentUserHostOrCoHost = [EUserType.HOST, EUserType.CO_HOST].includes(
      currentPeer?.peer_info?.user_type as EUserType
    );

    const assignor = peers.find(({ record_id }) => record_id === assignedBy?.recordId);
    const assignorName = assignor?.peer_info.peer_name;

    const isAssignedToCurrentUser = assignTo?.recordId === currentPeer?.record_id;
    const isDismissedByCurrentUser = currentPeer?.record_id === dismissedBy?.recordId;

    // Helper functions for better readability
    const getAnswerModeLabel = (answerType?: EAnswerType): string => {
      switch (answerType) {
        case EAnswerType.PUBLIC:
          return 'to reply publicly';
        case EAnswerType.PRIVATE:
          return 'to reply privately';
        default:
          return 'for live answering';
      }
    };

    const getDismissedLabel = (): string => {
      if (isDismissedByCurrentUser) return 'Dismissed by me';
      if (isViewer) return 'Your question is dismissed';
      if (dismissedBy && dismissedBy.name) return `Dismissed by ${dismissedBy.name}`;
      if (dismissedBy) {
        return 'Dismissed';
      }
      return '';
    };

    const getAssignedLabel = (): string => {
      if (isViewer) return '';

      const answerMode = getAnswerModeLabel(answerType);

      if (isAssignedToCurrentUser && isCurrentUserSpeaker) {
        return `Assigned to you ${answerMode} by ${assignorName}`;
      }

      if (isAssignedToCurrentUser && isCurrentUserHostOrCoHost) {
        return `Assigned to me ${answerMode}`;
      }

      const assignerSuffix = assignTo?.recordId === assignedBy?.recordId ? '' : ` by ${assignorName}`;

      return `Assigned to ${assignedToName} ${answerMode}${assignerSuffix}`;
    };

    const getAnsweredLabel = (): string => {
      const lowerAnswerType = answer?.answerType?.toLowerCase();

      if (isViewer) {
        return `Answered in ${lowerAnswerType}`;
      }

      const answerer = isAssignedToCurrentUser ? 'me' : assignedToName;
      return `Answered by ${answerer} as ${lowerAnswerType} answer`;
    };

    let label = '';

    switch (type) {
      case EQuestionStatus.DISMISSED:
        label = getDismissedLabel();
        break;

      case EAnswerStatus.ASSIGNED:
        label = getAssignedLabel();
        break;

      case EAnswerStatus.ANSWERED:
        label = getAnsweredLabel();
        break;

      default:
        break;
    }

    return {
      statusLabel: label,
      statusType: type,
    };
  }, [
    status,
    answer,
    assignTo,
    dismissedBy,
    assignedBy,
    currentPeer?.peer_info?.user_type,
    currentPeer?.record_id,
    peers,
  ]);

  return result;
};

export default useQuestionStatusLabel;
